import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { localStateReducer } from '../../../utils/common';
import { mergeMessageIntoData } from './wsMessageMerge';
import { useApolloClient, useLazyQuery } from '@apollo/client/react';
import { ITDTKData, TDTKQuery, TDTKVars } from '../../../apollo/types';
import { ServerError } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import type { WatchQueryFetchPolicy, ErrorLike } from '@apollo/client';
import { useSocketContext } from './SocketProvider';
import { useStateDispatch } from '../../../context/AppContext';
import ErrorPage from '../../nonDigital/error/ErrorPage';
import Spinner from '../../ui/loading/SpinnerWrapper';

/**
 * Mutate the globalData with relevant errors if necessary
 * @param globalData contains the entire data array of all merged data
 * @param dataSnippet contains the snippet of data that we recently received
 * @param error relevan error dealing with the dataSnippet
 * Note: we need the globalData to mutate it with relevant errors.
 */
// eslint-disable-next-line
export const errorHandler = (
  // eslint-disable-next-line
  dispatch: React.Dispatch<any>,
  // eslint-disable-next-line
  query: TDTKQuery<any>,
  showError: boolean,
  // eslint-disable-next-line
  globalData: any,
  // eslint-disable-next-line
  dataSnippet: any,
  error: Partial<ErrorLike>
) => {
  /** Extract GraphQL errors array from Apollo v4 CombinedGraphQLErrors. */
  // eslint-disable-next-line
  const getGraphQLErrors = (err: any): any[] | undefined => {
    if (CombinedGraphQLErrors.is(err)) return err.errors?.length ? [...err.errors] : undefined;
    return undefined;
  };

  const graphQLErrorsArray = getGraphQLErrors(error);
  const gqlErrorExists = !!(
    graphQLErrorsArray &&
    graphQLErrorsArray[0] &&
    graphQLErrorsArray[0].path &&
    graphQLErrorsArray[0].path[0]
  );

  // `TDTKQuery.kind` is the discriminated-union tag (e.g. 'CheckUserName') and serves as the
  // GraphQL operation name for RUM tagging.
  const operationName = query.kind;

  // eslint-disable-next-line
  const isGqlTypeError = (error: any) => {
    const gqlErrors = getGraphQLErrors(error);
    if (gqlErrors && gqlErrors.length > 0) {
      return gqlErrors.every((gqlError: { message: string }) => {
        const isGqlTypeError =
          gqlError.message.startsWith('Cannot return null') ||
          gqlError.message.startsWith('Expected value of type') ||
          gqlError.message.startsWith('Expected Iterable');

        return isGqlTypeError;
      });
    }
    return false;
  };

  // eslint-disable-next-line
  const isGqlServerError = (error: any) => {
    const gqlErrors = getGraphQLErrors(error);
    if (gqlErrors && gqlErrors.length > 0) {
      return gqlErrors.every((gqlError: { message: string }) => gqlError.message === 'server_error');
    }
    return false;
  };

  /**
   * Default error handler for all errors that are not handled by the other cases.
   */
  const defaultErrorHandler = () => {
    console.error('Unhandled GraphQL Error!');
    console.error('Failed GQL', query);
    console.error('Error Object', error);
    dispatch({
      globalError: <ErrorPage errorCode='500' error={error} source='graphql' query={operationName} />,
    });
  };

  /**
   * Handle Schema Type Validation Errors for graphql
   */
  // eslint-disable-next-line
  const handleGqlTypeError = (globalData: any, dataSnippet: any, error: Partial<ErrorLike>) => {
    const gqlErrors = getGraphQLErrors(error);
    const errorType = gqlErrors?.[0]?.path?.[0];
    // eslint-disable-next-line
    const indexErrMsgMap: { [key: number]: any } = {};

    switch (errorType) {
      //only support paginated students for now.
      //err.path is an array of the path to the error. i.e. "paginatedStudents.students.0"
      //where path[2] is the index of the student in the paginatedStudents.students array
      //when dealing with GqlTypeErrors, these are Schema Validation Errors.
      //therefore we are always referencing specific student records in the data that failed Schema Validation
      case ErrorType.PAGINATED_STUDENTS:
        gqlErrors
          ?.filter((err) => {
            return err.path && Array.isArray(err.path) && !isNaN(err.path?.[2]);
          })
          .map((err) => {
            const index = err.path?.[2];
            indexErrMsgMap[index] = {
              message: err.message,
              path: err.path,
            };
          });
        for (const index of Object.keys(indexErrMsgMap)) {
          console.error(
            'Validation Error for the following data',
            // eslint-disable-next-line
            dataSnippet.paginatedStudents.students[index as any],
            ' with the following error: ',
            // eslint-disable-next-line
            indexErrMsgMap[index as any]
          );
        }
        break;
      default:
        console.error('Unhandled GraphQL Type Error!');
        console.error('Failed GQL', query);
        console.error('Error Object', getGraphQLErrors(error));
        break;
    }
  };

  /**
   * Handle server_error from graphql
   */
  // eslint-disable-next-line
  const handleGqlServiceError = (globalData: any, error: Partial<ErrorLike>) => {
    const gqlErrors = getGraphQLErrors(error);
    const errorType = gqlErrors?.[0]?.path?.[0];

    switch (errorType) {
      case ErrorType.PAGINATED_STUDENTS:
        console.error('Failed GQL', query);
        console.error('Error Object', error);
        break;
      default:
        console.error('Unhandled GraphQL Server Error!');
        console.error('Failed GQL', query);
        console.error('Error Object', getGraphQLErrors(error));
        break;
    }
  };

  /** Safely parse the response body from a ServerError (v4 uses bodyText instead of result). */
  // eslint-disable-next-line
  const parseServerErrorBody = (serverError: ServerError): any => {
    try {
      return JSON.parse(serverError.bodyText);
    } catch {
      return {};
    }
  };

  //proceed to handle the error
  if (showError) {
    // Apollo v4: ServerError is thrown directly
    const serverError: ServerError | undefined = ServerError.is(error) ? error : undefined;

    if (serverError) {
      const parsedBody = parseServerErrorBody(serverError);
      const resErrorCode = parsedBody?.errors?.[0]?.code;
      const resStatusCode = serverError?.statusCode?.toString() || '500';
      if (resStatusCode >= '400' && resStatusCode <= '499') {
        // All 400 Errors
        if (resErrorCode === 'auth:missing_user_role' || resErrorCode === 'auth:missing_user_record') {
          console.error('auth user error', resErrorCode);
          dispatch({
            globalError: (
              <ErrorPage errorCode='auth:missing_user_record' error={error} source='graphql' query={operationName} />
            ),
          });
        } else if (resStatusCode === '403') {
          console.error('403', parsedBody);
          dispatch({
            globalError: <ErrorPage errorCode='403' error={error} source='graphql' query={operationName} />,
          });
        } else if (resStatusCode === '429') {
          dispatch({
            globalError: <ErrorPage errorCode='429' error={error} source='graphql' query={operationName} />,
          });
        } else {
          dispatch({
            globalError: <ErrorPage errorCode='400' error={error} source='graphql' query={operationName} />,
          });
        }
      } else if (resErrorCode === 'auth:missing_auth_info') {
        console.error('auth:missing_auth_info', parsedBody?.message);
        dispatch({
          globalError: <ErrorPage errorCode='400' error={error} source='graphql' query={operationName} />,
        });
      } else if (resStatusCode >= '500' && resStatusCode <= '599') {
        // All 500 errors
        console.error('resStatusCode = ', resStatusCode);
        dispatch({
          globalError: <ErrorPage errorCode='500' error={error} source='graphql' query={operationName} />,
        });
      }
    } else if (gqlErrorExists && graphQLErrorsArray) {
      graphQLErrorsArray.forEach((gqlError) => {
        const scopedError = new CombinedGraphQLErrors({ errors: [gqlError] });
        if (isGqlTypeError(scopedError)) {
          handleGqlTypeError(globalData, dataSnippet, scopedError);
        } else if (isGqlServerError(scopedError)) {
          handleGqlServiceError(globalData, scopedError);
        } else {
          defaultErrorHandler();
        }
      });
    } else {
      defaultErrorHandler();
    }
  }
};

type ChildrenProps<T extends ITDTKData> = (
  wsData: T | undefined,
  refetch: () => void,
  fetchMore: <TFetchData = T, TFetchVars = TDTKVars>(fetchMoreOptions: {
    variables?: Partial<TFetchVars>;
    // eslint-disable-next-line
    [key: string]: any;
  }) => void
) => React.ReactNode;

enum ErrorType {
  PAGINATED_STUDENTS = 'paginatedStudents',
}

function QueryComponent<T extends ITDTKData>({
  children,
  fetchPolicy = 'network-only',
  pollInterval = 0,
  query,
  skip = false,
  generateFetchMoreOptions,
  showError = true,
  renderImmediately = false,
}: {
  children: ChildrenProps<T>;
  fetchPolicy?: WatchQueryFetchPolicy;
  pollInterval?: number;
  query: TDTKQuery<T>;
  skip?: boolean;
  generateFetchMoreOptions?: (data: T | undefined) => void;
  showError?: boolean;
  renderImmediately?: boolean;
}) {
  // Global App state.
  const dispatch = useStateDispatch();
  const { lastJsonMessage } = useSocketContext();

  // Local state.
  const [localState, setLocalState] = React.useReducer(localStateReducer, {
    skip,
  });

  const options: {
    fetchPolicy: WatchQueryFetchPolicy;
    pollInterval: number;
    variables?: TDTKVars;
  } = {
    fetchPolicy,
    pollInterval,
  };
  if ('variables' in query) {
    options.variables = query.variables;
  }
  const [getData, { loading, error, data, refetch, fetchMore }] = useLazyQuery<T, TDTKVars>(
    query.specification,
    options
  );

  const [wsData, setWsData] = useState<T>();

  const client = useApolloClient();

  // Skip on re-renders.
  useEffect(() => {
    if (!data && !loading && !localState.skip) {
      // eslint-disable-next-line
      getData('variables' in query ? { variables: query.variables } : (undefined as any));
      setLocalState({
        skip: true,
      });
    }

    // eslint-disable-next-line
    query.resolvedData = data;
    const { hasUpdated, updatedQuery } = mergeMessageIntoData<T>(query, lastJsonMessage);

    if (hasUpdated) {
      // if our replacement apolloObject is relevant to websocket updates, merge it in
      client.writeQuery({
        query: query.specification,
        data: updatedQuery.resolvedData,
        ...('variables' in query ? { variables: query.variables } : {}),
        // eslint-disable-next-line
      } as any);
    }
    setWsData(query.resolvedData as T);
  }, [client, getData, lastJsonMessage, data, localState.skip, loading, query]);

  // In Apollo v4, fetchMore with updateQuery writes through the cache,
  // producing a new `data` reference each time. Using wsData directly as a
  // dependency would cause an infinite loop: data changes → setWsData →
  // fetchMore → cache write → new data → repeat.
  // Instead, derive a stable key from the data that only changes when there
  // is genuinely more data to fetch (i.e. lastKey changes).
  const fetchMoreKey = useMemo(() => {
    if (!wsData) return undefined;
    // Check all known paginated keys for a lastKey value
    // eslint-disable-next-line
    const paginatedData = (wsData as any)?.paginatedStudents ?? (wsData as any)?.paginatedStudentTestStatus;
    return paginatedData?.lastKey ?? null;
  }, [wsData]);

  const fetchMoreCallBack = useCallback(async () => {
    const options = generateFetchMoreOptions?.(wsData);
    if (options) {
      await fetchMore(options).then((result) => {
        if (result.error) {
          // eslint-disable-next-line
          errorHandler(dispatch, query, showError, data, result.data, result.error as any);
        }
      });
    }
    // eslint-disable-next-line
  }, [wsData, fetchMore, generateFetchMoreOptions]);

  useEffect(() => {
    fetchMoreCallBack();
    // eslint-disable-next-line
  }, [fetchMoreKey, fetchMore, generateFetchMoreOptions]);

  if (loading && !renderImmediately && !wsData) {
    return <Spinner />;
  }

  if (error) {
    //error is only ever set here if the first calls fails. Any fetchmore calls do not set error.
    //so data is the only relevant data snippet.
    errorHandler(dispatch, query, showError, data, data, error);
  }

  return children(wsData, refetch, fetchMore);
}

export default QueryComponent;
